import { ModalFooter } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { FC, useMemo, useState } from "react";
import { useUpdateWindhostingSite, useWindhostingSite } from "@hostingbv/lib/src/api/partner/PartnerHooks";
import { ValidationError } from "@hostingbv/lib/src/api/ModelFactory";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { Site } from "@hostingbv/lib/src/api/partner/models/Site";
import { SiteConfig } from "@hostingbv/lib/src/api/partner/models/windhosting/SiteConfig";
import { Input } from "@hostingbv/lib/src/components/form/Input";
import { useRefresh } from "@hostingbv/lib/src/components/RefreshController";

export const AddSiteAliasDomainModal: FC<{ site: Site }> = ({ site }) => {
  const [input, setInput] = useState("")
  const [deploymentError, setDeploymentError] = useState("")

  const { data: conf } = useWindhostingSite(site.serverId, site.id)
  const { mutate, error } = useUpdateWindhostingSite(site.serverId, site.id)
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!
  const refresh = useRefresh()

  const domains = useMemo(() => [...new Set(
    input.split(/[\s,]+/).map(domain => domain.trim()).filter(domain => domain.length > 0)
  )], [input])

  const duplicates = error instanceof ValidationError ? new Set(error.errors) : new Set<string>()
  const highlightedDomains = domains.some(domain => duplicates.has(domain))

  const save = async () => {
    if (!conf || domains.length === 0) return
    const deployment = await mutate(new SiteConfig(conf.certificate, conf.phpVersion, conf.phpPoolConfig, conf.webserverConfig, [...new Set([...conf.aliasDomains, ...domains])]))

    const updatedDeployment = await awaitNextDeploymentFinished(deployment.id)
    if (updatedDeployment?.status === "failed") {
      // @ts-expect-error output is untyped
      setDeploymentError(updatedDeployment?.output?.message ?? 'Invalid configuration')
      throw Error("deployment failed");
    }
    refresh()
  }

  return <>
    <form onSubmit={() => save()} className={"space-y-4 flex-1"}>
      <Input
        label={"Alias domains"}
        type={"textarea"}
        value={input}
        onChange={setInput}
        placeholder={"www.example.com\nshop.example.com"}
        focus={true}
      />
      {domains.length > 0 && <ul className={"space-y-1"}>
        {domains.map((domain, k) => (
          <li key={k} className={`text-sm px-3 py-1.5 rounded-sm ${duplicates.has(domain) ? "bg-red-50 text-red-900" : "bg-slate-100 text-slate-900"}`}>
            {domain}
            {duplicates.has(domain) && <span className={"ml-2 text-xs font-medium uppercase tracking-wide"}>Duplicate</span>}
          </li>
        ))}
      </ul>}
      {error instanceof ValidationError && !highlightedDomains && <div className={"px-3 py-2 bg-red-50 rounded-sm my-4 text-sm text-red-900"}>
        {error.message}
      </div>}
      {deploymentError !== '' && <div className={"px-3 py-2 bg-red-50 rounded-sm my-4 text-sm text-red-900"}>
        {deploymentError}
      </div>}
    </form>
    <ModalFooter text={"Add"} onSubmit={save} />
  </>
}
